import { Component, computed, effect, inject, input, signal, viewChild } from "@angular/core";
import { toSignal, toObservable } from "@angular/core/rxjs-interop";
import { ThreeDotMenuComponent } from "../three-dot-menu/three-dot-menu.component";
import { ListItemComponent } from "../three-dot-menu/list-item/list-item.component";
import { ICadastre } from "src/models/estate/key-info.model";
import { IMenuLink, SolutionPage, LinkHelperService, ILinkParams, UnitLinkType } from "src/services/shared/link-helper.service";
import { Router } from "@angular/router";
import { UnitNavigationInfoDto } from "src/models/units/unit-attributes.model";
import { UnitDataService } from "src/services/unit-data.service";
import { map, switchMap } from "rxjs/operators";
import { of } from "rxjs";
import { ILatLng } from "polydraw";

@Component({
    selector: "three-dot-navigation",
    standalone: true,
    imports: [ThreeDotMenuComponent, ListItemComponent],
    templateUrl: "./three-dot-navigation.component.html",
    styleUrls: ["./three-dot-navigation.component.scss"],
})
export class ThreeDotNavigationComponent {
    private readonly router = inject(Router);
    private readonly linkHelperService = inject(LinkHelperService);
    private readonly unitDataService = inject(UnitDataService);

    vc = viewChild(ThreeDotMenuComponent);

    linkParameters = input<ILinkParams>(null);
    unitId? = input<number>(null);
    solutionPage = input<SolutionPage>(null);
    /// Predefined menu items to show instead of generating them dynamically
    menuItems = input<IMenuLink[]>(null);

    private lookupUnitId = computed<number | null>(() => {
        let predefinedParams = this.linkParameters();
        let unitId = this.unitId();
        let visible = this.vc()?.isVisible();
        if (!visible || predefinedParams != null) {
            unitId = null;
        }
        return unitId != null ? unitId : null;
    });

    private lookupParams = toSignal(
        toObservable(this.lookupUnitId).pipe(
            switchMap((unitId) => {
                if (unitId != null) {
                    return this.unitDataService.getUnitNavigationInfoObservable(unitId).pipe(map((data) => this.infoToParams(data)));
                }
                return of(null);
            }),
        ),
        { initialValue: null },
    );

    protected navigationMenuItems = computed<IMenuLink[]>(() => {
        let visible = this.vc()?.isVisible;
        let menuItems = this.menuItems();
        let unitId = this.unitId();
        let linkParams = this.linkParameters();
        let solutionPage = this.solutionPage();
        let lookupParams = this.lookupParams();

        if (linkParams == null) {
            linkParams = lookupParams;
        }
        if (visible) {
            if (menuItems != null) {
                return menuItems;
            }
            if (solutionPage == null) {
                console.warn("ThreeDotNavigationComponent: solutionPage is null, cannot generate navigation menu items.");
                return [];
            }
            if (linkParams == null && unitId == null) {
                console.warn("ThreeDotNavigationComponent: no identity for estate, cannot generate navigation menu items.");
                return [];
            }
            if (linkParams != null) {
                return this.linkHelperService.getLinks(solutionPage, linkParams);
            }
        }
        return [];
    });

    protected handleMenuItemClick(link: IMenuLink): void {
        if (link.route?.url) {
            window.open(link.route.url, "_blank");
        } else if (link.route) {
            this.router.navigate([link.route.routerLink], { queryParams: link.route.routerLinkParams });
        }
    }
    private infoToParams(data: UnitNavigationInfoDto): ILinkParams | null {
        if (!data) return null;
        return {
            unitId: data.unitId,
            estateType: data.estateType,
            ownershipType: data.ownership,
            ownershipSubType: data.ownershipSubType,
            estateId: data.estateId,
            organizationNumber: data.organizationNumber,
            addressId: data.addressId,
            shareNumber: data.shareNumber,
            unitLinkType: UnitLinkType.Any,
            cadastre: {
                kNr: data.municipalityNumber,
                gNr: data.gNr,
                bNr: data.bNr,
                fNr: data.fNr,
                sNr: data.sNr,
            } as ICadastre,
            position: {
                lat: data.latitude,
                lng: data.longitude,
            } as ILatLng,
        };
    }
}
